import { T } from '@territory/tokens';

/*
 * The marketing site CTA buttons (apps/marketing src/lib/rv.jsx CTAButton), for Button variant="marketing-cta"
 * (primary) and variant="marketing-ghost" (ghost). Verbatim from the shipped inline style and the GlobalStyle
 * .rv-cta rules so the swap is a visual no-op. The sheet paints everything except `full` width, which is inline.
 * Colors read the --rv-* marketing roles in products.marketing; outside marketing each falls back to a base token.
 * Primary ink is the page bg (it flips with the theme), not accentText. Focus ring: the site's global
 * :focus-visible (2px accent, offset 3px, radius 4px).
 */

/* Every role the marketing CTA reads that is not a base token. products.marketing.roles must set each in both themes. */
export const MARKETING_BUTTON_ROLES = ['cta-shadow', 'cta-hover-shadow', 'cta-sheen'] as const;
export type MarketingButtonRole = (typeof MARKETING_BUTTON_ROLES)[number];

export const marketingRole = (name: MarketingButtonRole, fallback: string): string => `var(--rv-${name}, ${fallback})`;

/* Marketing roles that predate the base tokens and are shared by the whole site. */
const mk = (name: string, fallback: string): string => `var(--rv-${name}, ${fallback})`;

const EASE = 'cubic-bezier(0.16,1,0.3,1)';
const HOVER_MS = 150;
const SHEEN_MS = 560;
const ARROW_MS = 220;
const PRESS_MS = 80;
const NO_SHADOW = '0 0 0 transparent';

/* verbatim shipped CTAButton, off-scale */
const BASE = { gap: 9, track: '0.06em', r: 13, lh: 1.2 };
const SIZE = { md: { font: '0.72rem', pad: '12px 20px' }, lg: { font: '0.82rem', pad: '15px 26px' } };
const ARROW_NUDGE = 5;
const LIFT = 3;
export const MARKETING_ICON = { md: 15, lg: 17 };
export const MARKETING_ICON_WEIGHT = 2;

export function marketingButtonCss(pri: string, ghost: string): string {
  const both = `${pri}, ${ghost}`;
  const sel = (suffix: string): string => `${pri}${suffix}, ${ghost}${suffix}`;
  const sheen = marketingRole('cta-sheen', 'transparent');
  const tx = (props: string[], ms: number): string => props.map((p) => `${p} ${ms}ms ${EASE}`).join(', ');
  return `${both} { position: relative; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; gap: ${BASE.gap}px; max-width: 100%; font-family: ${T.fontDisplay}; font-weight: 700; letter-spacing: ${BASE.track}; text-transform: uppercase; white-space: normal; text-align: center; overflow-wrap: break-word; line-height: ${BASE.lh}; border-radius: ${BASE.r}px; text-decoration: none; cursor: pointer; transition: ${tx(['transform', 'box-shadow', 'background', 'border-color'], HOVER_MS)}; }
${sel('[data-rv-size="md"]')} { font-size: ${SIZE.md.font}; padding: ${SIZE.md.pad}; }
${sel('[data-rv-size="lg"]')} { font-size: ${SIZE.lg.font}; padding: ${SIZE.lg.pad}; }
${pri} { overflow: hidden; border: 1px solid transparent; background: ${T.accent}; color: ${T.bg}; box-shadow: ${marketingRole('cta-shadow', NO_SHADOW)}; }
${pri}::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(105deg, transparent 38%, ${sheen} 50%, transparent 62%); transform: translateX(-140%); transition: transform ${SHEEN_MS}ms ${EASE}; }
${ghost} { border: 1px solid ${mk('border', T.separatorBold)}; background: ${mk('s1', T.surfaceAlt)}; color: ${mk('text', T.label)}; box-shadow: none; }
${sel(' svg')} { transition: transform ${ARROW_MS}ms ${EASE}; }
@media (hover: hover) {
${pri}:hover { transform: translateY(-${LIFT}px); box-shadow: ${marketingRole('cta-hover-shadow', NO_SHADOW)}; }
${pri}:hover::after { transform: translateX(140%); }
${ghost}:hover { transform: translateY(-${LIFT}px); background: ${mk('s2', T.surfaceAlt)}; border-color: ${mk('border-hover', T.separatorBold)}; }
${sel(':hover svg')} { transform: translateX(${ARROW_NUDGE}px); }
}
${sel('[aria-disabled="true"]')} { opacity: 0.4; cursor: not-allowed; }
${sel(':focus-visible')} { outline: 2px solid ${T.accent}; outline-offset: 3px; border-radius: 4px; }
${sel(':active')} { transform: translateY(1px) scale(0.97); transition-duration: ${PRESS_MS}ms; }`;
}
